import {
  useGetBulkSessionVoteCounts,
  useGetSessionVoteCount,
  useGetSessionsByEventId,
  useGetUserSessionVotes,
  useMarkSessionsAsViewed,
  useRemoveVoteForSession,
  useVoteForSession,
} from '@sunrise-ui/api-client';
import {
  Colors,
  FirstVoteCelebrationModal,
  TextMono,
  isAndroid,
  isIos,
  useAuthStore,
  useDynamicStyles,
  useExecuteWithAuth,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import type { ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { IconButton as PaperIconButton } from 'react-native-paper';

type Props = {
  sessionId?: string;
  eventId?: string;
  communityId?: string;
  style?: ViewStyle;
  isLoading?: boolean;
};

export function SessionVoteButton({
  sessionId,
  eventId,
  communityId,
  style,
  isLoading,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { currentUserId } = useAuthStore();
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);

  // Get all sessions from the event to ensure full bulk data cache
  const { data: eventSessions } = useGetSessionsByEventId(
    eventId,
    undefined,
    communityId,
  );
  const sessionIds =
    eventSessions?.map((session) => session.id).filter(Boolean) || [];

  // Get bulk vote counts for ALL sessions from the event
  const { data: bulkVoteCountsData } = useGetBulkSessionVoteCounts({
    sessionIds,
    eventId: eventId || '', // Add eventId for consistent cache key
    enabled: sessionIds.length > 0,
  });

  // Look for this session's vote count in the bulk data
  const bulkVoteCount = bulkVoteCountsData?.counts?.find(
    (count) => count.session_id === sessionId,
  );

  // Fall back to individual query if bulk data isn't available
  const shouldFetchIndividual = !bulkVoteCount;

  const { data: individualVoteCountData, isLoading: isLoadingVoteCount } =
    useGetSessionVoteCount(shouldFetchIndividual ? sessionId : undefined);

  // Use bulk data if available, otherwise use individual query data
  const voteCountData = bulkVoteCount || individualVoteCountData;
  const numberOfVotes = voteCountData?.vote_count ?? 0;

  // Fetch user session votes directly
  const { data: userSessionVotesData } = useGetUserSessionVotes(
    eventId,
    currentUserId,
    communityId,
  );

  // Determine if user has voted by checking if sessionId exists in user's voted sessions
  const hasVoted = userSessionVotesData
    ? userSessionVotesData.some((session) => session.id === sessionId)
    : false;

  const { mutate: voteForSession, isPending: isPendingVote } =
    useVoteForSession(sessionId);
  const { mutate: removeVoteForSession, isPending: isPendingRemoveVote } =
    useRemoveVoteForSession(sessionId);
  const { mutate: markSessionsAsViewed } = useMarkSessionsAsViewed();

  // Hook to handle authenticated voting with data loading
  const { execute: executeVote, isExecuting: isVoting } = useExecuteWithAuth<
    ['add' | 'remove']
  >(
    async (voteIntent, complete) => {
      // Wait for user session votes data to be available
      if (!userSessionVotesData) {
        return; // Keep retrying when data loads
      }

      // Mark session as viewed when voting (for both vote and unvote)
      if (sessionId) {
        markSessionsAsViewed([sessionId]);
      }

      if (voteIntent === 'remove') {
        removeVoteForSession();
      } else {
        // Check if this is the user's first vote (before adding the new vote)
        const isFirstVote = userSessionVotesData.length === 0;

        voteForSession(undefined, {
          onSuccess: () => {
            // Show celebration modal if this was the user's first vote
            if (isFirstVote) {
              setShowCelebrationModal(true);
            }
          },
        });
      }

      complete();
    },
    {
      onError: (error) => {
        console.error('Error voting for session:', error);
      },
    },
  );

  const isLoadingState =
    isLoading ||
    isLoadingVoteCount ||
    isPendingVote ||
    isPendingRemoveVote ||
    isVoting;

  const handleVote = async () => {
    // Pass the intent based on current vote state
    await executeVote(hasVoted ? 'remove' : 'add');
  };

  // Determine which icon to show based on current state and loading state
  const displayHasVoted = isPendingVote
    ? true
    : isPendingRemoveVote
      ? false
      : hasVoted;

  const handleCloseCelebrationModal = async () => {
    setShowCelebrationModal(false);
  };

  return (
    <>
      <View style={[styles.voteContainer, style]}>
        <View style={styles.heartContainer}>
          <PaperIconButton
            icon={displayHasVoted ? 'heart' : 'heart-outline'}
            iconColor={displayHasVoted ? Colors.pink0 : Colors.text}
            size={24}
            onPress={() => {
              void handleVote();
            }}
            disabled={!sessionId || isLoadingState}
            style={[
              styles.heartIcon,
              isLoadingState && styles.heartIconLoading,
            ]}
          />
          {isLoadingVoteCount ? (
            <View style={styles.votesSkeleton} />
          ) : (
            <TextMono style={styles.voteCount}>{numberOfVotes}</TextMono>
          )}
        </View>
      </View>

      <FirstVoteCelebrationModal
        visible={showCelebrationModal}
        onClose={handleCloseCelebrationModal}
        eventId={eventId}
      />
    </>
  );
}

const createStyles = () =>
  StyleSheet.create({
    votesSkeleton: {
      width: 20,
      height: 15,
      backgroundColor: Colors.opacity20,
      borderRadius: 8,
      marginVertical: isAndroid ? 1.45 : isIos ? 2.5 : 3,
    },
    loadingContainer: {
      width: 24,
      height: 24,
      backgroundColor: Colors.opacity20,
      borderRadius: 12,
    },
    voteContainer: {
      alignItems: 'center',
    },
    heartContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    heartIcon: {
      margin: 0,
    },
    heartIconLoading: {
      opacity: 0.6,
    },
    voteCount: {
      marginLeft: 2,
      fontSize: 24,
      textAlign: 'center',
    },
  });
